import { NotificationGameType } from "@1games-ru/region-shared/dist/enums/notification/notification-cef";
import React, { PropsWithChildren } from "react";
import { cn } from "@/core/helpers/cn";
import { BrushIcon, CrossSmallIcon, HammerIcon } from "@/shared/icons";

function getIcon(type: NotificationGameType) {
  switch (type) {
    case NotificationGameType.BusinessCleaned:
    case NotificationGameType.BusinessCleanedPartly:
      return BrushIcon;

    case NotificationGameType.BusinessRepaired:
    case NotificationGameType.BusinessRepairedPartly:
      return HammerIcon;

    case NotificationGameType.BusinessCleaningZoneAbandoned:
    case NotificationGameType.BusinessHaveNoRepairTool:
    default:
      return CrossSmallIcon;
  }
}

function getVariant(type: NotificationGameType) {
  switch (type) {
    case NotificationGameType.BusinessCleaned:
    case NotificationGameType.BusinessRepaired:
    case NotificationGameType.FridgeSuccessfulDelivered:
      return "success";

    case NotificationGameType.BusinessCleanedPartly:
    case NotificationGameType.BusinessRepairedPartly:
      return "warning";

    case NotificationGameType.BusinessCleaningZoneAbandoned:
    case NotificationGameType.BusinessHaveNoRepairTool:
    case NotificationGameType.BusinessRepairingZoneAbandoned:
      return "error";

    default:
      return "info";
  }
}

const variants = {
  info: {
    color: "#5cd8ff",
    bg: "bg-gradient-to-r from-[rgba(92,216,255,0.2)] to-[rgba(18,18,18,0.02)]",
  },
  success: {
    color: "#5fff69",
    bg: "bg-gradient-to-r from-[rgba(95,255,105,0.2)] to-[rgba(18,18,18,0.02)]",
  },
  warning: {
    color: "#ffb45c",
    bg: "bg-gradient-to-r from-[rgba(255,154,92,0.2)] to-[rgba(18,18,18,0.02)]",
  },
  error: {
    color: "#ff4e4e",
    bg: "bg-gradient-to-r from-[rgba(215,52,52,0.2)] to-[rgba(18,18,18,0.02)]",
  },
};

type NotificationProps = {
  variant: "info" | "success" | "warning" | "error";
  icon: React.ReactElement;
  title: string;
  onClick?: () => void;
};

function NotificationIcon({
  className,
  children,
  color,
}: PropsWithChildren<{ className?: string; color: string }>) {
  return (
    <div
      className={cn(
        "w-[42px] h-[42px] border-2 border-current rounded-[14px] p-[2px] rotate-45",
        className,
      )}
      style={{ color }}
    >
      <div className="bg-current rounded-[10px] flex items-center justify-center size-full">
        {children}
      </div>
    </div>
  );
}

function Notification({ variant, icon, title, onClick }: NotificationProps) {
  const { bg, color } = variants[variant];

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className="relative w-full pointer-events-auto"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <NotificationIcon className="absolute z-1 top-0 -left-[29px]" color={color}>
        {React.cloneElement(icon, {
          className: cn("-rotate-45 text-dark-800", icon.props.className),
          size: icon.props.size || 20,
        })}
      </NotificationIcon>

      <div
        className="bg-[#12121299] rounded-xl rounded-tl-none rounded-bl-none relative w-fit"
        style={{
          // Cut the notch from the left side
          clipPath: `path("M500 .001v42H4a8 8 0 0 1-4-1 14 14 0 0 0 5-3.5l6.6-6.8a14 14 0 0 0 0-19.4l-6.5-6.8a14 14 0 0 0-5.1-3.5 8 8 0 0 1 4-1h496Z")`,
        }}
      >
        <div className={cn("p-3 px-5 flex items-center pl-7", bg)}>
          <div className="text-14 whitespace-nowrap">{title}</div>
        </div>
      </div>
    </div>
  );
}

export function NotificationsGameItem({
  type,
  text,
  onClick,
}: {
  type: NotificationGameType;
  text: string;
  onClick?: () => void;
}) {
  const variant = getVariant(type);
  const Icon = getIcon(type);

  return <Notification variant={variant} icon={<Icon />} title={text} onClick={onClick} />;
}
